import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuthStore } from '../store/authStore';

export const ResetPassword = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const clearSession = useAuthStore((s) => s.clear);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('Lien invalide');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Les deux mots de passe ne correspondent pas');
      return;
    }
    setIsLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, password }, { authenticated: false });
      // Sécurité : si une session était déjà active dans ce navigateur, on la coupe.
      // Si un attaquant avait un access token volé, il est invalidé de facto à expiration
      // (15 min) — on le force ici à passer par le nouveau mot de passe.
      clearSession();
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Erreur inattendue');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-semibold text-neutral-900">Falko</h1>
          <p className="mt-2 text-sm text-neutral-500">Choisis ton nouveau mot de passe</p>
        </div>

        {success ? (
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6 text-center">
            <div className="text-3xl mb-2">✅</div>
            <h2 className="text-lg font-semibold text-neutral-900 mb-2">Mot de passe mis à jour</h2>
            <p className="text-sm text-neutral-600">Redirection vers la connexion…</p>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6 space-y-4"
          >
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-1">
                Nouveau mot de passe (8 caractères minimum)
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>
            <div>
              <label
                htmlFor="passwordConfirm"
                className="block text-sm font-medium text-neutral-700 mb-1"
              >
                Confirmation
              </label>
              <input
                id="passwordConfirm"
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-lg bg-violet-600 text-white py-2 text-sm font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {isLoading ? 'Mise à jour…' : 'Mettre à jour mon mot de passe'}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-neutral-600">
          <Link to="/login" className="text-violet-600 hover:text-violet-700 font-medium">
            Retour à la connexion
          </Link>
        </p>
      </div>
    </div>
  );
};
